import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Grid, CircularProgress, IconButton } from "@mui/material";
import CustomTable from "../../common/CustomTable";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { invoiceInfo } from "../../../redux/slice/invoice/InvoiceSlice";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  Cancel,
  CloudDownloadOutlined,
} from "@mui/icons-material";
import {
  isAgencyCommissionType,
  isV3CommissionType,
  priceFormatter,
  toCommissionTypeRequestKey,
} from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import CustomDatePicker from "../../common/CustomDatePicker";
import dayjs from "dayjs";
import {
  CommissionInfo,
  getPaymentsByInvoice,
  PaymentElementInfo,
  RecordPayoutInfo,
  saveMultipleCommissions,
} from "../../../redux/slice/payment/paymentSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import CustomDateRange from "../../common/CustomDateRange";
import { DateObject } from "react-multi-date-picker";
import { useNavigation } from "../../../hooks/useNavigation";
import { useLocation } from "react-router-dom";
import {
  COMMISSION_TYPE_AGENCY_KEY,
  PAYMENT_STATUS,
} from "../../../constants/applicationConstants";
import InfoPopup from "../../common/InfoPopup";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

interface Row {
  invoice: string;
  date: string;
  invAmount: string;
  paid: string;
  v3Comm: string;
  agencyComm: string;
  invStatus: string;
  v3PaidAmount: string;
  agencyPaidAmount: string;
  remainingAmount: string;
}

type InvoicePayoutTotals = {
  invoicePaidAmount: number;
  hasV3Commission: boolean;
  v3Expected: number;
  v3Received: number;
  agencyExpected: number;
  agencyReceived: number;
  remainingAgencyPayout: number;
  agencyNames: string[];
};

const roundCurrency = (amount: number) => Math.round(amount * 100) / 100;

const emptyInvoiceTotals = (): InvoicePayoutTotals => ({
  invoicePaidAmount: 0,
  hasV3Commission: false,
  v3Expected: 0,
  v3Received: 0,
  agencyExpected: 0,
  agencyReceived: 0,
  remainingAgencyPayout: 0,
  agencyNames: [],
});

const aggregatePaymentCommissions = (
  payments: PaymentElementInfo[],
  allCodeTablesValuesMap: any
): InvoicePayoutTotals => {
  let invoicePaidAmount = 0;
  let hasV3Commission = false;
  let v3Expected = 0;
  let v3Received = 0;
  let agencyExpected = 0;
  let agencyReceived = 0;
  let remainingAgencyPayout = 0;
  const agencyNames: string[] = [];

  payments
    .filter((payment) => payment.paymentStatus !== PAYMENT_STATUS.VOID)
    .forEach((payment) => {
      invoicePaidAmount += Number(payment.paymentAmount) || 0;

      payment.commissions?.forEach((commission: CommissionInfo) => {
        const expected = Number(commission.expectedCommissionAmount) || 0;
        const received = Number(commission.calculatedCommissionAmount) || 0;

        if (isV3CommissionType(allCodeTablesValuesMap, commission.agencyType)) {
          hasV3Commission = true;
          v3Expected += expected;
          v3Received += received;
        } else if (
          isAgencyCommissionType(allCodeTablesValuesMap, commission.agencyType)
        ) {
          agencyExpected += expected;
          agencyReceived += received;
          remainingAgencyPayout += Math.max(0, expected - received);

          if (
            commission.agencyName &&
            !agencyNames.includes(commission.agencyName)
          ) {
            agencyNames.push(commission.agencyName);
          }
        }
      });
    });

  return {
    invoicePaidAmount: roundCurrency(invoicePaidAmount),
    hasV3Commission,
    v3Expected: roundCurrency(v3Expected),
    v3Received: roundCurrency(v3Received),
    agencyExpected: roundCurrency(agencyExpected),
    agencyReceived: roundCurrency(agencyReceived),
    remainingAgencyPayout: roundCurrency(remainingAgencyPayout),
    agencyNames,
  };
};

/** The invoice fields the agency payout form reads — satisfied by `invoiceInfo`. */
export type AgencyPayoutInvoice = Pick<
  invoiceInfo,
  "invoiceId" | "mfgInvoiceNumber" | "mfgInvoiceDate" | "invoiceAmount" | "invoiceStatus"
> & { agencyName?: string };

type RecordPayoutFormProps = {
  selectedInvoices?: AgencyPayoutInvoice[];
  onCancel: () => void;
  onRecorded: () => void;
  /** Set when rendered inside a raised dialog so its own popups/pickers open above it. */
  overlayZIndex?: number;
};

export const RecordPayoutForm = ({
  selectedInvoices,
  onCancel,
  onRecorded,
  overlayZIndex,
}: RecordPayoutFormProps) => {
  const { control, handleSubmit, setValue, getValues, watch, formState } =
    useForm<RecordPayoutInfo>({
      mode: "onChange",
      defaultValues: {
        payment: undefined as unknown as number,
        payoutDate: "",
        payoutComment: "",
        fromDate: undefined,
        toDate: undefined,
      },
    });
  const dispatch = useAppDispatch();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const columns = useMemo<Column[]>(
    () => [
      { id: "invoice", label: "INV #", minWidth: 100 },
      { id: "date", label: "INV Date", minWidth: 100 },
      { id: "invAmount", label: "INV Amount", minWidth: 100 },
      { id: "paid", label: "INV Paid Amt", minWidth: 100 },
      { id: "invStatus", label: "INV Status", minWidth: 100 },
      { id: "v3Comm", label: "V3 Revenue", minWidth: 100 },
      {
        id: "v3PaidAmount",
        label: "V3 Rev Received",
        minWidth: 100,
      },
      { id: "agencyComm", label: "AGY Svc Fee", minWidth: 100 },
      { id: "agencyPaidAmount", label: "AGY Svc Fees Paid", minWidth: 100 },
      { id: "remainingAmount", label: "Remaining Payout", minWidth: 100 },
    ],
    []
  );
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    datepickerWrapSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const [navigatedInvList, setNavigateInvList] = useState<AgencyPayoutInvoice[]>();
  const [invoicePayoutMap, setInvoicePayoutMap] = useState<
    Record<number, InvoicePayoutTotals>
  >({});
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [showV3WarningPopup, setShowV3WarningPopup] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<RecordPayoutInfo | null>(
    null
  );

  const watchedPayoutDate = watch("payoutDate");

  useEffect(() => {
    const invList = selectedInvoices;
    setNavigateInvList(invList);

    if (!invList?.length) {
      setInvoicePayoutMap({});
      return;
    }

    const fetchPaymentDetails = async () => {
      setIsLoadingPayments(true);
      const totalsByInvoice: Record<number, InvoicePayoutTotals> = {};

      await Promise.all(
        invList.map(async (invoice) => {
          try {
            const response = await dispatch(getPaymentsByInvoice(invoice.invoiceId));
            const payments =
              (response.payload as { data?: { payments?: PaymentElementInfo[] } })
                ?.data?.payments ?? [];

            totalsByInvoice[invoice.invoiceId] =
              aggregatePaymentCommissions(payments, allCodeTablesValuesMap);
          } catch {
            totalsByInvoice[invoice.invoiceId] = emptyInvoiceTotals();
          }
        })
      );

      setInvoicePayoutMap(totalsByInvoice);
      setIsLoadingPayments(false);
    };

    fetchPaymentDetails();
  }, [dispatch, selectedInvoices, allCodeTablesValuesMap]);

  const agencyDisplayLabel = useMemo(() => {
    const agencyNames = Array.from(
      new Set(
        Object.values(invoicePayoutMap).flatMap((totals) => totals.agencyNames)
      )
    );

    if (agencyNames.length > 0) {
      return agencyNames.length === 1
        ? agencyNames[0]
        : agencyNames.join(", ");
    }

    if (!navigatedInvList?.length) return "";

    const invoiceAgencyNames = Array.from(
      new Set(
        navigatedInvList
          .map((invoice) => invoice.agencyName)
          .filter((name): name is string => Boolean(name))
      )
    );

    if (invoiceAgencyNames.length === 0) return "Selected Invoices";
    if (invoiceAgencyNames.length === 1) return invoiceAgencyNames[0];
    return invoiceAgencyNames.join(", ");
  }, [invoicePayoutMap, navigatedInvList]);

  const payoutSummary = useMemo(() => {
    const invList: Row[] = [];
    let totalRemainingAmount = 0;
    let hasUnpaidV3Commission = false;

    navigatedInvList?.forEach((invoice) => {
      const totals = invoicePayoutMap[invoice.invoiceId];
      if (!totals) return;

      totalRemainingAmount += totals.remainingAgencyPayout;

      if (
        totals.hasV3Commission &&
        roundCurrency(totals.v3Expected - totals.v3Received) > 0
      ) {
        hasUnpaidV3Commission = true;
      }

      invList.push({
        invoice: String(invoice.mfgInvoiceNumber),
        date: invoice.mfgInvoiceDate,
        invAmount: priceFormatter(Number(invoice.invoiceAmount)),
        paid: priceFormatter(totals.invoicePaidAmount),
        invStatus: String(invoice.invoiceStatus),
        v3Comm: totals.hasV3Commission
          ? priceFormatter(totals.v3Expected)
          : "-",
        v3PaidAmount: totals.hasV3Commission
          ? priceFormatter(totals.v3Received)
          : "-",
        agencyComm: priceFormatter(totals.agencyExpected),
        agencyPaidAmount: priceFormatter(totals.agencyReceived),
        remainingAmount: priceFormatter(totals.remainingAgencyPayout),
      });
    });

    return {
      rows: invList,
      totalRemainingAmount: roundCurrency(totalRemainingAmount),
      hasUnpaidV3Commission,
    };
  }, [navigatedInvList, invoicePayoutMap]);

  // The payment amount is not entered by the user: it is always the full
  // remaining payout. Keep the (disabled) field and the form value in sync.
  useEffect(() => {
    setValue("payment", payoutSummary.totalRemainingAmount, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [payoutSummary.totalRemainingAmount, setValue]);

  const isPayoutDateValid = Boolean(watchedPayoutDate);
  const canSubmitPayout =
    !isLoadingPayments &&
    payoutSummary.totalRemainingAmount > 0 &&
    isPayoutDateValid &&
    !formState.errors.payoutDate;

  const setInvoiceIds = (): number[] =>
    navigatedInvList?.map((invoice) => invoice.invoiceId) ?? [];

  const buildPayoutPayload = (formData: RecordPayoutInfo): RecordPayoutInfo => ({
    invoiceIds: setInvoiceIds(),
    commissionAgencyType: toCommissionTypeRequestKey(
      allCodeTablesValuesMap,
      COMMISSION_TYPE_AGENCY_KEY
    ) as RecordPayoutInfo["commissionAgencyType"],
    payment: payoutSummary.totalRemainingAmount,
    payoutDate: formData.payoutDate,
    payoutComment: formData.payoutComment?.trim() || "",
    fromDate: getValues("fromDate") || undefined,
    toDate: getValues("toDate") || undefined,
  });

  const submitPayout = async (formData: RecordPayoutInfo) => {
    const payload = buildPayoutPayload(formData);

    const response = await dispatch(saveMultipleCommissions(payload));
    if (response.payload && response.payload.message === "SUCCESS") {
      const base64Data = response?.payload?.data?.base64String;
      const alertObj: SingleAlertInfo = base64Data
        ? { message: "Agency payments recorded successfully!", alertType: "success" }
        : {
            message: "Agency payments were recorded, but no payout file was returned to download.",
            alertType: "warning",
          };

      // Only download when the server actually sent a file — atob(null) would save a 3-byte "null.null".
      if (base64Data) {
        const FileName = response?.payload?.data?.fileName || "Agency_Payout";
        const FileType = response?.payload?.data?.fileType || "xlsx";

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${FileName}.${FileType}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }

      dispatch(setSingleAlertObj(alertObj));
      onRecorded();
    }
  };

  const onSubmit = async (formData: RecordPayoutInfo) => {
    if (payoutSummary.hasUnpaidV3Commission) {
      setPendingFormData(formData);
      setShowV3WarningPopup(true);
      return;
    }

    await submitPayout(formData);
  };

  const handlePayoutDate = (date: Date | null) => {
    setValue("payoutDate", dayjs(date).format("MM/DD/YYYY"), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    setValue("fromDate", fromDate ? fromDate.format("MM/DD/YYYY") : "");
    setValue("toDate", toDate ? toDate.format("MM/DD/YYYY") : "");
  };

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.9, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box data-testid="card" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={cardSx}>
            <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 1.5, pb: 2.5 }}>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: mTokens.textPrimary, mb: 1.5 }}>
                <strong>Agency:</strong> {agencyDisplayLabel}
              </Typography>

              {isLoadingPayments ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} sx={{ color: mTokens.accent }} />
                </Box>
              ) : (
                <Box sx={{ overflowX: "auto", width: "100%" }}>
                  <CustomTable
                    columns={columns}
                    rows={payoutSummary.rows}
                    disablePagination={true}
                    borderBox={true}
                  />
                </Box>
              )}

              <Box sx={{ textAlign: "right", marginTop: 2 }}>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: mTokens.textPrimary }}>
                  {priceFormatter(payoutSummary.totalRemainingAmount)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={cardSx}>
            <Box sx={{ px: { xs: 2, sm: 3.2 }, pt: 2.2, pb: 3.2 }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Payment" />
                  <Box sx={inputFieldSx()}>
                    <CustomCurrencyField
                      control={control}
                      controllerName="payment"
                      label="Payment Amount"
                      isDisabled
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Payout Date" required />
                  <Box sx={datepickerWrapSx(!!formState.errors.payoutDate)}>
                    <CustomDatePicker
                      control={control}
                      onChange={handlePayoutDate}
                      controllerName="payoutDate"
                      label="Payout Date"
                      rules={{ required: "Payout Date is required." }}
                      size="small"
                      popperZIndex={overlayZIndex !== undefined ? overlayZIndex + 100 : undefined}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Payout Comment" />
                  <Box sx={inputFieldSx()}>
                    <CustomTextField
                      control={control}
                      controllerName="payoutComment"
                      label="Payout Comment"
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Expected Pay Date" />
                  <Box
                    sx={(theme) => {
                      const wrap = datepickerWrapSx() as Record<string, any>;
                      return {
                        ...wrap,
                        "& .MuiOutlinedInput-root": {
                          ...(wrap["& .MuiOutlinedInput-root"] || {}),
                          height: 40,
                          minHeight: 40,
                        },
                        "& .MuiInputBase-input": {
                          ...(wrap["& .MuiInputBase-input"] || {}),
                          padding: "8px 12px",
                          fontSize: "0.88rem",
                        },
                      };
                    }}
                  >
                    <CustomDateRange
                      label="Expected Pay Date Range"
                      control={control}
                      controllerName="dateRange"
                      onChange={handleDateRangeChange}
                      zIndex={overlayZIndex !== undefined ? overlayZIndex + 100 : undefined}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={1.2}
                    justifyContent="flex-end"
                    mt={1}
                  >
                    <Button
                      fullWidth
                      color="warning"
                      type="submit"
                      removeUpperCase
                      startIcon={<CloudDownloadOutlined />}
                      disabled={!canSubmitPayout}
                      sx={{
                        ...primaryBtnSx,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: 240 },
                        backgroundColor: mTokens.accent,
                        boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                        "& .MuiTypography-root": { color: "#ffffff !important" },
                        "& .MuiButton-startIcon": { color: "#ffffff" },
                      }}
                    >
                      Record and Download Payout
                    </Button>
                    <Button
                      color="error"
                      variant="outlined"
                      removeUpperCase
                      startIcon={<Cancel />}
                      onClick={onCancel}
                      sx={{
                        ...cancelBtnSx,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: 140 },
                        "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                        "& .MuiButton-startIcon": { color: mTokens.error },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </form>

      {showV3WarningPopup && (
        <InfoPopup
          open={showV3WarningPopup}
          onClose={() => {
            setShowV3WarningPopup(false);
            setPendingFormData(null);
          }}
          content="Revenues to V3 have not been received yet for one or more selected invoices. You can still proceed with the agency service fee payout by clicking 'Confirm' to continue."
          severity="warning"
          title="Warning"
          confirmButton="Confirm"
          zIndex={overlayZIndex !== undefined ? overlayZIndex + 100 : undefined}
          onConfirm={async () => {
            setShowV3WarningPopup(false);
            if (pendingFormData) {
              const formData = pendingFormData;
              setPendingFormData(null);
              await submitPayout(formData);
            }
          }}
        />
      )}
    </>
  );
};

const RecordPayout = () => {
  const location = useLocation();
  const { navigate } = useNavigation();
  const { tokens: mTokens } = useModalStyles();
  const selectedInvoices = location.state?.selectedInvoices as AgencyPayoutInvoice[] | undefined;

  return (
    <Box
      sx={{
        mx: { xs: "10px", sm: "20px" },
        mt: "12px",
        pb: "72px",
      }}
    >
      <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} />
        <Box data-testid="header">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Billing / Record Payout
          </Typography>
          <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            Record Payout
          </Typography>
        </Box>
      </Box>

      <RecordPayoutForm
        selectedInvoices={selectedInvoices}
        onCancel={() => navigate(-1)}
        onRecorded={() => navigate(-1)}
      />
    </Box>
  );
};

export default RecordPayout;